import { Service } from '@toeverything/infra';

import type { WorkspaceMetadata } from '..';
import type { WorkspaceDestroyService } from './destroy';
import type { WorkspaceFactoryService } from './factory';
import type { WorkspaceFlavoursService } from './flavours';
import type { WorkspaceListService } from './list';
import type { WorkspaceProfileService } from './profile';
import type { WorkspaceRepositoryService } from './repo';
import type { WorkspaceTransformService } from './transform';

const DUPLICATE_DESTINATION_TIMEOUT = 60_000;

const abortable = <T>(promise: Promise<T>, signal: AbortSignal): Promise<T> => {
  if (signal.aborted) {
    return Promise.reject(signal.reason);
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', onAbort);
    });
  });
};

export class WorkspacesService extends Service {
  get list() {
    return this.listService.list;
  }

  constructor(
    private readonly flavoursService: WorkspaceFlavoursService,
    private readonly listService: WorkspaceListService,
    private readonly profileRepo: WorkspaceProfileService,
    private readonly transform: WorkspaceTransformService,
    private readonly workspaceRepo: WorkspaceRepositoryService,
    private readonly workspaceFactory: WorkspaceFactoryService,
    private readonly destroy: WorkspaceDestroyService
  ) {
    super();
  }

  get deleteWorkspace() {
    return this.destroy.deleteWorkspace;
  }

  get getProfile() {
    return this.profileRepo.getProfile;
  }

  get transformLocalToCloud() {
    return this.transform.transformLocalToCloud;
  }

  duplicateWorkspace = async (
    metadata: WorkspaceMetadata,
    copyName: string
  ) => {
    const { workspace, dispose } = this.workspaceRepo.open({ metadata });
    let duplicateMetadata: WorkspaceMetadata | undefined;

    try {
      duplicateMetadata = await this.transform.duplicateWorkspace(
        workspace,
        copyName
      );
      if (duplicateMetadata.flavour !== 'local') {
        try {
          await this.finalizeCloudDuplicate(duplicateMetadata);
        } catch (error) {
          await this.destroy.deleteWorkspace(duplicateMetadata).catch(() => {
            // Cleanup is best-effort; retain the original finalization error.
          });
          throw error;
        }
      }
      return duplicateMetadata;
    } finally {
      dispose();
    }
  };

  private readonly finalizeCloudDuplicate = async (
    metadata: WorkspaceMetadata
  ) => {
    const { workspace, dispose } = this.workspaceRepo.open({ metadata });
    const abortController = new AbortController();
    const timeout = setTimeout(() => {
      abortController.abort(
        new Error('Timed out while uploading duplicated workspace')
      );
    }, DUPLICATE_DESTINATION_TIMEOUT);
    const removePriorities: Array<() => void> = [];

    try {
      await workspace.engine.doc.waitForDocReady(
        workspace.id,
        abortController.signal
      );

      const docIds = Array.from(workspace.docCollection.docs.keys());
      for (const docId of docIds) {
        const doc = workspace.docCollection.getDoc(docId);
        if (!doc) {
          throw new Error(`Duplicated document ${docId} is unavailable`);
        }
        doc.load();
        removePriorities.push(workspace.engine.doc.addPriority(docId, 100));
        await workspace.engine.doc.waitForDocReady(
          docId,
          abortController.signal
        );
      }

      const blobList = await abortable(
        workspace.engine.blob.storage.list(),
        abortController.signal
      );
      for (const { key } of blobList) {
        const blob = await abortable(
          workspace.engine.blob.storage.get(key),
          abortController.signal
        );
        if (!blob) {
          throw new Error(`Duplicated blob ${key} is unavailable`);
        }
        await abortable(
          workspace.engine.blob.upload(blob),
          abortController.signal
        );
      }

      await workspace.engine.doc.waitForSynced(
        undefined,
        abortController.signal
      );
    } finally {
      clearTimeout(timeout);
      removePriorities.forEach(removePriority => removePriority());
      dispose();
    }
  };

  get open() {
    return this.workspaceRepo.open;
  }

  get openByWorkspaceId() {
    return this.workspaceRepo.openByWorkspaceId;
  }

  get create() {
    return this.workspaceFactory.create;
  }

  async getWorkspaceBlob(meta: WorkspaceMetadata, blob: string) {
    return await this.flavoursService.flavours$.value
      .find(x => x.flavour === meta.flavour)
      ?.getWorkspaceBlob(meta.id, blob);
  }

  getWorkspaceFlavourProvider(meta: WorkspaceMetadata) {
    return this.flavoursService.flavours$.value.find(
      x => x.flavour === meta.flavour
    );
  }

  getAllWorkspaceProfile() {
    const list = this.listService.list.workspaces$.value;
    const profiles = list.map(meta => this.getProfile(meta));
    return profiles;
  }
}
